import * as swcHelpers from '@swc/helpers';
import { CommerceToolsApi } from '@grandvisionhq/commercetools';
import { Helpers } from '@grandvisionhq/inventory';
import { getLocalizedAttrValue, getAttrValue, isPresent, getStringAttrValue, getLocalizedAttrValues, getJoinedLocalizedAttrValues, getLocalizedValue, getNestedProperty } from '@grandvisionhq/utils';
import { getDiscount } from './utils/get-discount.js';
import { getDiscountDescription } from './utils/get-discount-description.js';
import { getIsAvailableForSubscription } from './utils/get-is-available-for-subscription.js';
import { getPrice } from './utils/get-price.js';
import { getProductTag } from './utils/get-product-tag.js';
import { isVariantPublished } from './utils/is-variant-published.js';
import { mapToPurchaseTypePrices } from './utils/map-to-purchase-type-prices.js';

// using hardcoded english locale here to map:
// https://github.com/GrandVisionHQ/gv-core-components/blob/69612677a059e714191bee6c94b62f7866e039f1/packages/common/src/ColorPicker/color-mapping.tsx#L1
const COLOR_MAPPING_LOCALE = 'en-GB';
const getExtrasId = (product)=>{
    if (!product || !product.masterVariant || !Array.isArray(product.masterVariant.attributes)) {
        return undefined;
    }
    const extrasAttribute = product.masterVariant.attributes.find((attr)=>attr.name === 'lensExtras'
    );
    if (!extrasAttribute || !extrasAttribute.value) {
        return undefined;
    }
    return extrasAttribute.value.id;
};
function getProductTypeName(product) {
    const result = product && product.productType && product.productType.obj && CommerceToolsApi.isProductTypeName(product.productType.obj.name) ? product.productType.obj.name : false;
    return !!result ? result : undefined;
}
function getProductModelName(variant, locale, productType) {
    if (CommerceToolsApi.isLenses(productType)) {
        return getLocalizedAttrValue(variant, 'materialType', locale);
    }
    return variant.name || '';
}
function getActiveVariant(product, sku) {
    if (!sku) {
        return product.masterVariant;
    }
    if (product.masterVariant.sku === sku) {
        return product.masterVariant;
    }
    return product.variants.find((variant)=>variant.sku === sku
    ) || product.masterVariant;
}
function getAvailability(variant) {
    return variant.availability ? {
        availableQuantity: Helpers.getAvailableQuantity(variant.availability)
    } : {};
}
const getLensPropertyValue = (variant, attrName)=>{
    const value = getAttrValue(variant, attrName);
    // We only care about numbers and strings
    if (!isPresent(value) || !(typeof value === 'number' || typeof value === 'string')) {
        return undefined;
    }
    return String(value);
};
function getVariantInfo(product, productType, locale, variant) {
    var ref2, ref1;
    const activeVariant = !variant || typeof variant === 'string' ? getActiveVariant(product, variant) : variant;
    const allowBackOrderAttribute = getAttrValue(activeVariant, 'allowBackOrder');
    const allowBackOrder = typeof allowBackOrderAttribute === 'undefined' ? undefined : !!allowBackOrderAttribute;
    const sharedProductInfo = {
        sku: activeVariant.sku || '',
        modelName: getProductModelName(activeVariant, locale, productType),
        modelCode: getStringAttrValue(activeVariant, 'brandModelCode'),
        header: getLocalizedAttrValue(activeVariant, 'header', locale),
        description: getLocalizedAttrValue(activeVariant, 'description', locale),
        images: getFullImageUrls(activeVariant),
        usps: getLocalizedAttrValues(activeVariant, 'usps', locale) || [],
        price: getPrice(activeVariant.prices),
        purchaseTypePrices: mapToPurchaseTypePrices(activeVariant.prices, locale, (ref2 = activeVariant.pricePerUnit) === null || ref2 === void 0 ? void 0 : ref2.amount, (ref1 = activeVariant.pricePerUnitSubscription) === null || ref1 === void 0 ? void 0 : ref1.amount),
        brand: String(getAttrValue(activeVariant, 'brand')),
        discountText: getDiscountDescription(activeVariant.prices, locale),
        discounted: getDiscount(activeVariant.prices, locale),
        tag: getProductTag(activeVariant, locale),
        allowBackOrder,
        excludedFromCheckout: !!getAttrValue(activeVariant, 'excludedFromCheckout'),
        channelAvailability: activeVariant.channelAvailability,
        isPublished: isVariantPublished(activeVariant),
        bestPrice: activeVariant.bestPrice,
        loyaltyPoints: activeVariant.loyaltyPoints,
        loyaltyPointsWithSubscription: activeVariant.loyaltyPointsWithSubscription
    };
    var _isOfferedWithPrescriptions;
    ({
        name: activeVariant.name || '',
        frameColorLabel: getJoinedLocalizedAttrValues(activeVariant, 'frameColor', locale),
        frameColor: getJoinedLocalizedAttrValues(activeVariant, 'frameColor', COLOR_MAPPING_LOCALE),
        frameShape: getLocalizedAttrValue(activeVariant, 'frameShape', locale),
        frameMaterial: getLocalizedAttrValue(activeVariant, 'frameMaterial', locale),
        gender: getLocalizedAttrValues(activeVariant, 'gender', locale) || [
            'UNISEX'
        ],
        isKids: false,
        isOfferedWithPrescriptions: (_isOfferedWithPrescriptions = activeVariant.isOfferedWithPrescriptions) !== null && _isOfferedWithPrescriptions !== void 0 ? _isOfferedWithPrescriptions : false,
        sku: activeVariant.sku || ''
    });
    const genderKeys = getLocalizedAttrValues(activeVariant, 'gender', locale);
    if (genderKeys && Array.isArray(genderKeys) && genderKeys.length) {
        genderKeys.some((a)=>a.toLowerCase() === 'kids'
        );
    }
    // Contact-lenses
    if (CommerceToolsApi.isLenses(productType)) {
        const info = swcHelpers.objectSpread({}, sharedProductInfo, {
            productType: 'Contact-lenses',
            brand: getLocalizedValue(product.name, locale),
            replacementFrequency: getLocalizedAttrValue(activeVariant, 'replacementFrequency', locale),
            materialType: getLocalizedAttrValue(activeVariant, 'materialType', locale),
            lensType: getLocalizedAttrValue(activeVariant, 'lensType', locale),
            material: getLocalizedAttrValue(activeVariant, 'material', locale),
            waterContent: getLocalizedAttrValue(activeVariant, 'waterContent', locale),
            packageQuantity: Number(getAttrValue(activeVariant, 'packageQuantity')),
            features: {
                hydration: !!getAttrValue(activeVariant, 'cbHydration'),
                softness: !!getAttrValue(activeVariant, 'cbSoftness'),
                breathability: !!getAttrValue(activeVariant, 'cbBreathability'),
                fatigue: !!getAttrValue(activeVariant, 'cbFatigue'),
                uv: !!getAttrValue(activeVariant, 'cbUV'),
                basics: !!getAttrValue(activeVariant, 'cbBasics')
            },
            lensProperties: {
                power: getLensPropertyValue(activeVariant, 'power'),
                baseCurve: getLensPropertyValue(activeVariant, 'baseCurve'),
                diameter: getLensPropertyValue(activeVariant, 'diameter'),
                axis: getLensPropertyValue(activeVariant, 'axis'),
                cylinder: getLensPropertyValue(activeVariant, 'cylinder'),
                addition: getLensPropertyValue(activeVariant, 'addition'),
                color: getLensPropertyValue(activeVariant, 'color')
            },
            isAvailableForSubscription: getIsAvailableForSubscription(activeVariant, sharedProductInfo.purchaseTypePrices)
        });
        return info;
    } else {
        const availability = getAvailability(activeVariant);
        const info = swcHelpers.objectSpread({}, sharedProductInfo, {
            contactLensCareType: getLocalizedAttrValue(activeVariant, 'contactLensCareType', locale),
            packageOption: String(getAttrValue(activeVariant, 'packageOption')),
            productType: 'Solutions',
            description: getLocalizedValue(product.description, locale),
            targetUsers: getLocalizedAttrValue(activeVariant, 'targetUsers', locale),
            convenience: getLocalizedAttrValue(activeVariant, 'convenience', locale),
            keyInstructions: getLocalizedAttrValue(activeVariant, 'keyInstructions', locale),
            features: {
                cleaning: !!getAttrValue(activeVariant, 'cbCleaning'),
                comfort: !!getAttrValue(activeVariant, 'cbComfort'),
                disinfect: !!getAttrValue(activeVariant, 'cbDisinfect'),
                hydration: !!getAttrValue(activeVariant, 'cbHydration'),
                rinsing: !!getAttrValue(activeVariant, 'cbRinsing'),
                sensitivity: !!getAttrValue(activeVariant, 'cbSensitivity')
            },
            /**
       * @deprecated pricePerLiter should not be used. Use pricePerUnit instead.
       */ pricePerLiter: activeVariant.pricePerUnit,
            pricePerUnit: activeVariant.pricePerUnit
        }, {
            availability
        }, {
            isAvailableForSubscription: getIsAvailableForSubscription(activeVariant, sharedProductInfo.purchaseTypePrices)
        });
        return info;
    }
}
var ref;
const getVariants = (variants, slug, productType, locale)=>{
    return variants.filter(isVariantPublished).map((variant)=>{
        var ref3;
        return {
            name: `${variant.brand} ${variant.name}`,
            sku: variant.sku || '',
            slug,
            productType,
            description: getVariantDescription(variant, locale),
            imageUrl: (ref = (ref3 = getFullImageUrls(variant)[0]) === null || ref3 === void 0 ? void 0 : ref3.url) !== null && ref !== void 0 ? ref : ''
        };
    });
};
const getVariantDescription = (variant, locale)=>[
        'lensColor',
        'frameColor',
        'accessoryColor'
    ].map((attr)=>getJoinedLocalizedAttrValues(variant, attr, locale)
    ).filter((str)=>str.trim() !== ''
    ).join(' / ')
;
function getFullImageUrls(variant) {
    const images = getNestedProperty(variant, 'media.images', []);
    return images;
}
/**
 * This helper returns a composition of the
 * `brand`, `brandModel` en `brandModelCode` attributes for a variant
 */ const getProductTitle = (productType = '', product, variant, locale)=>{
    const brand = productType.toLowerCase() === 'contact-lenses' ? getLocalizedValue(product.name || '', locale) : String(getAttrValue(variant, 'brand'));
    var _masterVariant;
    const replacementFrequency = getLocalizedAttrValue((_masterVariant = product.masterVariant) !== null && _masterVariant !== void 0 ? _masterVariant : {}, 'replacementFrequency', locale) || '';
    const brandModel = getAttrValue(variant, 'brandModel') || '';
    const brandModelCode = getAttrValue(variant, 'brandModelCode') || '';
    const brandModelColorCode = getAttrValue(variant !== null && variant !== void 0 ? variant : {}, 'brandModelColorCode') || '';
    return `${replacementFrequency} ${brand} ${brandModel} ${brandModelCode !== brandModel ? brandModelCode : ''} ${brandModelColorCode}`.replace(/\s+/g, ' ').trim();
};
const isLightProductModel = (product)=>'objectID' in product
;
const isFullVariantModel = (variant)=>'id' in variant
;

export { getActiveVariant, getExtrasId, getFullImageUrls, getLensPropertyValue, getProductModelName, getProductTitle, getProductTypeName, getVariantDescription, getVariantInfo, getVariants, isFullVariantModel, isLightProductModel };
//# sourceMappingURL=helpers.js.map
